import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useSystemStats, usePersonalStats } from '@/lib/queries/stats';
import { Card, Button, ErrorState, EmptyState } from '@rhpc/ui';
import {
  Activity,
  Clock,
  PlusCircle,
  CheckCircle2,
  Wallet,
  Users,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { PageHeader, PageShell } from '@/components/PageShell';

function formatRM(amount: number) {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
  }).format(amount);
}

function StatCard({
  label,
  icon,
  value,
  caption,
  tint,
}: {
  label: string;
  icon: ReactNode;
  value: ReactNode;
  caption: string;
  tint?: 'green';
}) {
  return (
    <Card className={tint === 'green' ? 'bg-ok-soft/70' : undefined}>
      <div className="flex items-center justify-between">
          <span className="text-eyebrow">{label}</span>
          <span className={tint === 'green' ? 'text-ok' : 'text-text-3'}>{icon}</span>
        </div>
        <div
          className={`mt-2 font-mono text-[30px] font-semibold tracking-tight ${
            tint === 'green' ? 'text-ok' : 'text-text'
          }`}
        >
          {value}
        </div>
        <div className="mt-1 text-[13px] text-text-3">{caption}</div>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <PageShell>
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="skeleton h-7 w-40 rounded-[5px]" />
          <div className="skeleton mt-[10px] h-[13px] w-[260px] rounded" />
        </div>
        <div className="skeleton h-9 w-[120px] shrink-0 rounded-md" />
      </div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="p-[20px_22px]">
            <div className="skeleton h-3 w-3/5 rounded" />
            <div className="skeleton mt-[14px] h-8 w-2/5 rounded-md" />
            <div className="skeleton mt-[14px] h-[11px] w-[70%] rounded" />
          </Card>
        ))}
      </div>
      <Card className="p-[24px_28px]">
        <div className="skeleton mb-6 h-4 w-[220px] rounded-[5px]" />
        <div className="skeleton h-[200px] w-full rounded-md" />
      </Card>
    </PageShell>
  );
}

function DashboardError({ onRetry }: { onRetry: () => void }) {
  return (
    <ErrorState
      title="Couldn't load your dashboard"
      happened="Something went wrong while fetching your claims."
      doing="You can retry the request now."
      youCan="Check your connection and try again. If it keeps happening, contact IT support."
      actions={
        <>
          <Button onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" strokeWidth={2} />
            Try again
          </Button>
          <Button variant="secondary">Contact IT</Button>
        </>
      }
    />
  );
}

export function DashboardPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdminOrFinance = role === 'ADMIN' || role === 'IT_ADMIN' || role === 'FINANCE' || role === 'FINANCE_LEAD';

  if (isAdminOrFinance) {
    return <AdminFinanceDashboard />;
  }
  return <StaffDashboard />;
}

// =======================================================
// STAFF DASHBOARD
// =======================================================
function StaffDashboard() {
  const { data: stats, isLoading, isError, refetch } = usePersonalStats();

  if (isLoading) return <DashboardSkeleton />;
  if (isError || !stats) return <PageShell><DashboardError onRetry={() => refetch()} /></PageShell>;

  return (
    <PageShell>
      <PageHeader
        title="Welcome back"
        description="Here's an overview of your recent claim activity."
        actions={
          <Link to="/claims">
            <Button className="gap-2">
              <PlusCircle className="h-4 w-4" strokeWidth={2.2} />
              New Claim
            </Button>
          </Link>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
        <StatCard
          label="Draft Claims"
          icon={<Activity className="h-4 w-4" strokeWidth={2} />}
          value={stats.myDraftsCount}
          caption="Pending submission editing"
        />
        <StatCard
          label="Pending Actions"
          icon={<Clock className="h-4 w-4" strokeWidth={2} />}
          value={stats.myPendingCount}
          caption="Awaiting managerial/finance processing"
        />
        <StatCard
          label="Approved YTD"
          icon={<CheckCircle2 className="h-4 w-4" strokeWidth={2} />}
          value={formatRM(stats.ytdApprovedRM)}
          caption="Total payout volume this year"
          tint="green"
        />
      </div>

      <Card className="p-[24px_28px]">
        <h2 className="mb-5 text-[15px] font-semibold">Your Claim History (Last 6 Months)</h2>
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.trendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="var(--rgx-grid)" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--rgx-text-3)', fontSize: 13 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--rgx-text-3)', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
                tickFormatter={(val) => `RM ${val}`}
              />
              <Tooltip
                formatter={(value) => [formatRM(Number(value) || 0), 'Total Amount']}
                contentStyle={{
                  borderRadius: '12px',
                  border: '1px solid var(--rgx-border)',
                  boxShadow: 'var(--rgx-shadow-sm)',
                  fontSize: '13px',
                  fontFamily: 'Manrope, system-ui, sans-serif',
                }}
              />
              <Line
                type="monotone"
                dataKey="totalAmount"
                stroke="var(--rgx-chart)"
                strokeWidth={2.5}
                dot={{ r: 4.5, strokeWidth: 2.5, fill: 'var(--rgx-surface)', stroke: 'var(--rgx-chart)' }}
                activeDot={{ r: 5.5, strokeWidth: 2.5, stroke: 'var(--rgx-chart)', fill: 'var(--rgx-chart)' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </PageShell>
  );
}

// =======================================================
// ADMIN / FINANCE DASHBOARD
// =======================================================
function AdminFinanceDashboard() {
  const { data: stats, isLoading, isError, refetch } = useSystemStats();
  const role = useAuthStore((s) => s.user?.role);
  const isFinance = role === 'FINANCE' || role === 'FINANCE_LEAD';

  if (isLoading) return <DashboardSkeleton />;
  if (isError || !stats) return <PageShell><DashboardError onRetry={() => refetch()} /></PageShell>;
  if (stats.totalUsers === 0 && stats.pendingClaimsCount === 0 && stats.pendingFinanceRM === 0) {
    return (
      <PageShell>
        <EmptyState
          title="No system activity yet"
          description="No staff have submitted claims this period. Data will appear here once the first claim moves through the queue."
        />
      </PageShell>
    );
  }

  const payoutSettled = stats.pendingFinanceRM === 0;

  return (
    <PageShell>
      <PageHeader
        title="System Analytics"
        description="Operational global statistics and queue metrics."
        actions={
          isFinance ? (
            <Link to="/finance/claims-queue">
              <Button variant="outline" className="gap-2">
                <Wallet className="h-4 w-4" strokeWidth={2} />
                Finance Queue
              </Button>
            </Link>
          ) : (
            <Link to="/admin/all-claims">
              <Button variant="outline" className="gap-2">
                <Activity className="h-4 w-4" strokeWidth={2} />
                All System Claims
              </Button>
            </Link>
          )
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
        <StatCard
          label="Active Users"
          icon={<Users className="h-4 w-4" strokeWidth={2} />}
          value={stats.totalUsers}
          caption="Total staff actively enrolled"
        />
        <StatCard
          label="Active Pending Pipeline"
          icon={<Clock className="h-4 w-4" strokeWidth={2} />}
          value={stats.pendingClaimsCount}
          caption="Submitted claims across all states"
        />
        <Card>
            <div className="flex items-center justify-between">
              <span className="text-eyebrow">Pending Finance Payout</span>
              <Wallet className="h-4 w-4 text-text-3" strokeWidth={2} />
            </div>
            <div className="mt-2 font-mono text-[30px] font-semibold tracking-tight">
              {formatRM(stats.pendingFinanceRM)}
            </div>
            <div
              className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${
                payoutSettled ? 'text-ok' : 'text-warn'
              }`}
            >
              {payoutSettled ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.2} />
                  All caught up, nothing owed
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.2} />
                  Awaiting finance action
                </>
              )}
            </div>
        </Card>
      </div>

      <Card className="p-[24px_28px]">
        <h2 className="mb-5 text-[15px] font-semibold">System Claim Volume (Last 6 Months)</h2>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.trendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="var(--rgx-grid)" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--rgx-text-3)', fontSize: 13 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--rgx-text-3)', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
                tickFormatter={(val) => `RM ${val}`}
              />
              <Tooltip
                formatter={(value) => [formatRM(Number(value) || 0), 'Total Volume']}
                contentStyle={{
                  borderRadius: '12px',
                  border: '1px solid var(--rgx-border)',
                  boxShadow: 'var(--rgx-shadow-sm)',
                  fontSize: '13px',
                  fontFamily: 'Manrope, system-ui, sans-serif',
                }}
              />
              <Line
                type="monotone"
                dataKey="totalAmount"
                stroke="var(--rgx-chart)"
                strokeWidth={2.5}
                dot={{ r: 4.5, strokeWidth: 2.5, fill: 'var(--rgx-surface)', stroke: 'var(--rgx-chart)' }}
                activeDot={{ r: 5.5, strokeWidth: 2.5, stroke: 'var(--rgx-chart)', fill: 'var(--rgx-chart)' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </PageShell>
  );
}
